// Login.jsx - Shows only left section on mobile
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LoginFormLS from "../../components/login/LoginFormLS";
import OTPModal from "../../components/login/OTPModal";
import ToastAlert from "../../components/common/ToastAlert";
import RightSection from "../../components/login/RightSection";
import { useAuth } from "../../hooks/useAuth";

const Login = () => {
  const [showOTP, setShowOTP] = useState(false);
  const [otpAction, setOtpAction] = useState("login");
  const [emailForOTP, setEmailForOTP] = useState("");
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const {
    login,
    verifyLoginOtp,
    sendForgotPasswordOTP,
    verifyResetOtp,
    loading,
    error,
    successMessage,
  } = useAuth();

  useEffect(() => {
    if (location.state?.resetSuccess) {
      setShowSuccessToast(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleSignIn = async (email, password, rememberMe) => {
    const result = await login(email, password, rememberMe);

    if (result.success && result.requiresOtp) {
      setEmailForOTP(email);
      setOtpAction("login");
      setShowOTP(true);
    } else if (!result.success) {
      console.log(" Login failed:", result.error);
    }
  };

  const handleSendOTPFromForgot = async (email) => {
    const result = await sendForgotPasswordOTP(email);

    if (result.success) {
      setEmailForOTP(email);
      setOtpAction("forgot");
      setShowOTP(true);
    }
  };

  const handleVerifyOTP = async (otpCode) => {
    if (otpAction === "login") {
      const result = await verifyLoginOtp(emailForOTP, otpCode);

      if (result.success) {
        setShowOTP(false);
        console.log("Login successful!");
        navigate("/dashboard");
      }
    } else if (otpAction === "forgot") {
      const result = await verifyResetOtp(emailForOTP, otpCode);

      if (result.success) {
        setShowOTP(false);
        navigate("/reset-password", {
          state: {
            email: emailForOTP,
            otp: otpCode,
          },
        });
      }
    }
  };

  const handleCloseOTP = () => {
    setShowOTP(false);
    console.log("📱 OTP modal closed");
  };

  return (
    <>
      <div className="flex h-screen">
        {/* Left Section - Always visible on all devices */}
        <div className="flex-1 bg-bg-main flex flex-col items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-primary-dark rounded-full animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 5}s`,
                }}
              />
            ))}
          </div>

          <div className="absolute top-20 right-20 w-64 h-64 bg-primary-cyan/5 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-primary-cyan/5 rounded-full blur-3xl animate-pulse-slow animation-delay-1000"></div>

          <div className="relative z-10 animate-fade-in-up animation-delay-200">
            <LoginFormLS
              onSignIn={handleSignIn}
              onSendOTP={handleSendOTPFromForgot}
              isLoading={loading}
              errorMessage={error}
              successMessage={successMessage}
            />
          </div>
        </div>

        {/* Right Section - Hidden on mobile, visible on desktop */}
        <div className="hidden lg:flex flex-1 bg-primary-dark flex items-center justify-center">
          <RightSection />
        </div>
      </div>

      {showOTP && (
        <OTPModal
          onClose={handleCloseOTP}
          onVerify={handleVerifyOTP}
          action={otpAction}
          email={emailForOTP}
          isLoading={loading}
        />
      )}

      {showSuccessToast && (
        <ToastAlert
          message="Password reset successfully! You can now login."
          type="success"
          duration={5000}
          onClose={() => setShowSuccessToast(false)}
        />
      )}
    </>
  );
};

export default Login;